import { WEIGHT_UNITS } from '@seedplanner/shared-utils';
// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezoneDayJs from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { type CropSeedDatesAndTrays, type TraysPerCrop, groupCropSeedDatesAndTrays } from './SeedNeededTable';

dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezoneDayJs);

export const generateSeedNeededPdf = async (
  jsPDF: typeof import('jspdf').jsPDF,
  {
    datesAndTrays,
    title,
    subtitle,
    downloadTitle,
    timezone,
  }: {
    readonly datesAndTrays: readonly CropSeedDatesAndTrays[];
    readonly title: string;
    readonly subtitle?: string;
    readonly downloadTitle: string;
    readonly timezone: string;
  },
) => {
  const doc = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
  });

  // Page dimensions and settings
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - 2 * margin;
  const borderRadius = 2; // Border radius
  const lineSpacing = 10; // Spacing between lines
  let startY = subtitle ? 54 : 44; // Initial Y position for the first todo item
  let currentPage = 1; // Track current page number

  const drawBorderAndFooter = () => {
    // Draw a rounded rectangle border
    doc.roundedRect(margin, margin, contentWidth, pageHeight - 2 * margin, borderRadius, borderRadius);
    // Page number
    doc.setFontSize(10);
    doc.text(`Page ${currentPage}`, pageWidth - margin, pageHeight - margin / 2, { align: 'right' });
    // App name
    doc.text('Microgreen Manager', pageWidth / 2, pageHeight - margin / 2, { align: 'center' });
  };

  const addNewPage = () => {
    doc.addPage();
    currentPage += 1;
    startY = currentPage === 1 ? 50 : margin + 10; // Adjust startY for title and date exclusion on subsequent pages
    drawBorderAndFooter(); // Draw border and footer for the new page
  };

  // Draw initial border and footer
  drawBorderAndFooter();

  if (currentPage === 1) {
    // Title and subtitle
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(title, pageWidth / 2, 30, { align: 'center' });
    if (subtitle) {
      doc.setFontSize(12);
      doc.text(subtitle, pageWidth / 2, 40, { align: 'center' });
    }
  }

  const datesAndTraysGroupedByDate = groupCropSeedDatesAndTrays(datesAndTrays);
  const sortedTrayGroups = [...datesAndTraysGroupedByDate];
  sortedTrayGroups.sort((a, b) => a.time - b.time);

  // Set font for todos
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(12);

  const formatCropTraysSeed = (item: TraysPerCrop): string => {
    const trays = item.traysForCrop;
    const crop = item.crop.crop_name;
    const seed = item.seedForCrop;
    const seedUnit =
      item.crop.sow_density_unit === WEIGHT_UNITS.GRAMS
        ? 'g'
        : item.crop.sow_density_unit === WEIGHT_UNITS.OUNCES
          ? 'oz'
          : item.crop.sow_density_unit;
    const traysIsPlural = trays > 1;
    const seedPerTray = item.crop.sowing_density;
    const soakStatus = item.crop.soakStatus;
    const hasSoak = soakStatus !== '';

    // This is always called client-side so no need to use formatNumber
    return `${crop} - ${trays.toLocaleString()} ${traysIsPlural ? 'trays' : 'tray'} - ${seed.toLocaleString()}${seedUnit} (${seedPerTray} / tray)${hasSoak ? ` - ${soakStatus} Soak` : ''}`;
  };

  for (const trayGroup of sortedTrayGroups) {
    if (startY > pageHeight - margin - 10) {
      addNewPage();
    }

    // Print date subheader
    doc.setFontSize(14);
    const trayGroupDate = dayjs.unix(trayGroup.time).tz(timezone).format('ddd, MMM D');
    const totalTrays = trayGroup.seedTrayGroup.totalTrays;
    doc.text(`${trayGroupDate} - ${totalTrays} trays`, margin + 10, startY);
    startY += 8; // Space before listing items

    const sortedTraysPerCrop = [...trayGroup.seedTrayGroup.traysPerCrop];
    sortedTraysPerCrop.sort((a, b) => a.crop.crop_name.localeCompare(b.crop.crop_name));

    sortedTraysPerCrop.forEach((traysPerCrop, idx) => {
      const splitText = doc.splitTextToSize(formatCropTraysSeed(traysPerCrop), contentWidth - 15) as string[]; // Split text if it overflows
      splitText.forEach((line, lineIndex) => {
        if (startY > pageHeight - margin - 10) {
          addNewPage();
        }
        doc.setFontSize(12);

        // Checkbox for the first line of split text only
        if (lineIndex === 0) {
          doc.rect(20, startY, 5, 5);
        }

        // Adjust text start position based on whether it's the first line of split text
        doc.text(line, 30, startY + 4);

        startY += lineSpacing; // Increment startY for the next line/todo item or notes
      });

      const isLastTodoForDate = idx === sortedTraysPerCrop.length - 1;
      if (isLastTodoForDate) {
        startY += lineSpacing - 2; // Space between dates
      }
    });
  }

  // Save the PDF
  doc.save(`${downloadTitle}.pdf`);
};
